import { useState } from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import CardActionButton from 'src/components/CardActionButton';
import Iconify from 'src/components/Iconify';
import { handleZipFileDownload, policyArtifactZipName } from 'src/utils/download.util';
import { WDAC_POLICY_PURPOSE } from 'src/utils/enums/wdacPolicyPurpose';
import { useDownloadWin32AppCatalog, useWin32AppPolicy } from 'src/hooks/wdac';

const GENERATING_MESSAGE =
  'This catalog is still being generated. Try the download again in a moment.';

const IMPORTS_DISABLED_TOOLTIP =
  "This app's trust includes no files, since every file set import is disabled, so there is no catalog to download.";

// Downloading a trusted app's catalog, and creating or updating it, which goes through the trust
// form's artifact step rather than a form of its own. A catalog still there after every import was
// disabled is out of step with the trust, so it cannot be downloaded.
const Win32CatalogActions = ({ globalApplicationVersionId, importsDisabled = false, onUpdate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  const { data: policyState, isLoading } = useWin32AppPolicy({
    globalApplicationVersionId,
    enabled: !!globalApplicationVersionId,
  });
  const { mutateAsync: downloadCatalog } = useDownloadWin32AppCatalog();

  const hasActiveCatalog = (policyState?.policies || []).some(policy => policy.isActive);

  const onDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadCatalog({ globalApplicationVersionId });
      handleZipFileDownload(
        blob,
        policyArtifactZipName(WDAC_POLICY_PURPOSE.Win32Apps, policyState?.currentVersion),
      );
    } catch {
      // The download fails until the generation job has written the catalog, which is a wait
      // rather than an error. The API client drops the status, so every failure reads as that wait.
      enqueueSnackbar(GENERATING_MESSAGE, { variant: 'info', autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Stack direction="row" gap={1} flexShrink={0}>
      {hasActiveCatalog ? (
        <Tooltip
          title={importsDisabled ? IMPORTS_DISABLED_TOOLTIP : 'Download security catalog'}
          arrow>
          {/* A disabled button swallows hover events, so the tooltip hangs off a wrapper. */}
          <Box component="span" display="inline-flex">
            {/* Labelled with the version it downloads, which is the latest one generated. */}
            <CardActionButton
              aria-label="Download security catalog"
              disabled={downloading || importsDisabled}
              onClick={onDownload}
              startIcon={<Iconify icon="mdi:download-outline" width={15} height={15} />}>
              {policyState?.currentVersion || 'Catalog'}
            </CardActionButton>
          </Box>
        </Tooltip>
      ) : null}
      <CardActionButton
        onClick={onUpdate}
        startIcon={
          <Iconify icon={hasActiveCatalog ? 'mdi:refresh' : 'mdi:plus'} width={15} height={15} />
        }>
        {hasActiveCatalog ? 'Update Trusted Artifacts' : 'Build Trusted Artifacts'}
      </CardActionButton>
    </Stack>
  );
};

export default Win32CatalogActions;
